import { useState, useEffect, useContext } from 'react';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { ProductCustomService, ProductOption } from '../../clients/customer-api/product/customer-product';
import { Spinner } from '../../components/spinner/spinner';
import { SelectForm } from '../../components/select-form/select-form';
import { Typography } from '../../components/typography/typography';
import { QuantityInput } from '../product-form/quantity-input/quantity-input';
import { displayMoneyFormat } from '../../util/util';
import { Location } from '../../clients/quipli-service/location/location';
import { TenantContext } from '../../context/tenant-context';

export type OptionSelection = { id: number; name: string };

export const Customizations = ({
  productId,
  isRental,
  location,
  hideServicePrices,
  selectedOptions,
  selectedServices,
  setSelectedOptions,
  setSelectedServices,
}: {
  productId: number;
  isRental: boolean;
  location: Location;
  hideServicePrices: boolean;
  selectedServices: {
    [serviceId: number]: ProductCustomService & {
      quantity?: number;
      selectedOption?: { label: string; value: string };
    };
  };
  selectedOptions: { [optionId: number]: { option: ProductOption; selection?: OptionSelection } };
  setSelectedServices: (s: {
    [serviceId: number]: ProductCustomService & {
      quantity?: number;
      selectedOption?: { label: string; value: string };
    };
  }) => void;
  setSelectedOptions: (o: { [optionId: number]: { option: ProductOption; selection?: OptionSelection } }) => void;
}) => {
  const { contextPath } = useContext(TenantContext);
  const [isLoading, setIsLoading] = useState(true);
  const [services, setServices] = useState<ProductCustomService[]>([]);
  const [options, setOptions] = useState<ProductOption[]>([]);
  const { getProductCustomizations } = useCustomerAPI();

  const init = async () => {
    setIsLoading(true);
    const customizations = await getProductCustomizations(productId);
    setServices(customizations.services);
    setOptions(customizations.options);
    const selected = customizations.options.reduce((acc, o) => {
      acc[o.id] = { option: o };
      return acc;
    }, {} as { [optionId: number]: { option: ProductOption; selection?: OptionSelection } });
    setSelectedOptions(selected);
    setIsLoading(false);
  };
  useEffect(() => {
    init();
  }, []);

  return isLoading ? (
    <Spinner />
  ) : (
    <div>
      {/* options displayed for both rental and slae */}
      <div className="product-options md:grid md:grid-cols-2 gap-2 rounded-md p-3 overflow-visible">
        {options.map((option) => (
          <div key={option.id} className="w-full-sm">
            <label>{option.name}</label>
            <SelectForm
              options={option.values}
              value={selectedOptions[option.id]?.selection}
              getOptionLabel={(o) => o.name}
              getOptionValue={(o) => `${o.id}`}
              onChange={(ov) =>
                setSelectedOptions({
                  ...selectedOptions,
                  [option.id]: { option, selection: ov as OptionSelection },
                })
              }
              isClearable={true}
            />
          </div>
        ))}
      </div>
      {/* services displayed for sale only */}
      {!isRental && (
        <div className="custom-services my-1 border-b block rounded-md p-3">
          {services.map((service) =>
            service.options ? (
              <div key={service.id} className="flex mb-2  justify-between items-end object-contain">
                <div className="w-[40px] h-[40px] m-1">
                  <img src={`${contextPath}${service.image}`} className="object-contain" />
                </div>
                <SelectForm
                  label={service.name}
                  className="min-w-[100px]"
                  getOptionValue={(o) => o.value}
                  getOptionLabel={(o) => o.label}
                  options={service.options.map((o) => ({ value: o, label: o }))}
                  value={selectedServices[service.id]?.selectedOption}
                  onChange={(selectedOption) =>
                    setSelectedServices({
                      ...selectedServices,
                      [service.id.toString()]: { ...service, selectedOption },
                    })
                  }
                  isClearable={true}
                />
                {!hideServicePrices && (
                  <div className="ml-2 p-3">
                    <Typography type="text-sm" weight="semi-bold">
                      +{displayMoneyFormat(service.price, location.currency)}
                    </Typography>
                  </div>
                )}
              </div>
            ) : (
              <div key={service.id} className="flex mb-2  justify-between items-center">
                <div className="w-[40px] h-[40px] m-1">
                  <img src={`${contextPath}${service.image}`} className="object-contain" />
                </div>
                <Typography type="text-sm" weight="medium">
                  {service.name}
                </Typography>
                <div className="flex items-end justify-end">
                  <div className={`quantity-wrapper-wrapper`}>
                    <QuantityInput
                      name="serviceQuantity"
                      value={selectedServices[service.id]?.quantity?.toString() ?? '0'}
                      onChange={(quantity) =>
                        setSelectedServices({
                          ...selectedServices,
                          [service.id]: { ...service, quantity: parseFloat(quantity ?? '0') },
                        })
                      }
                    />
                  </div>
                  {!hideServicePrices && (
                    <div className="ml-2 p-3">
                      <Typography type="text-sm" weight="semi-bold">
                        +{displayMoneyFormat(service.price, location.currency)}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
};
